/**
 * 设备适配器 - 统一的通信接口
 * 根据环境变量自动选择 WebHID 或 WebSocket 实现
 */
import { WebHIDDevice } from './webHIDDevice.js'
import { WebSocketDevice } from './websocketDevice.js'

const DEVICE_MODE = import.meta.env.VITE_DEVICE_MODE || 'webhid'

export class DeviceAdapter {
  constructor() {
    console.log(`[DeviceAdapter] 初始化设备适配器 - 模式: ${DEVICE_MODE}`)

    // 根据环境变量选择实现
    if (DEVICE_MODE === 'websocket') {
      this.device = new WebSocketDevice()
      this.mode = 'websocket'
      console.log('[DeviceAdapter] 使用 WebSocket 模式 (上位机通信)')
    } else {
      this.device = new WebHIDDevice()
      this.mode = 'webhid'
      console.log('[DeviceAdapter] 使用 WebHID 模式 (浏览器直连)')
    }
  }

  /**
   * 连接设备
   * @returns {Promise<boolean>}
   */
  async connect() {
    try {
      return await this.device.connect()
    } catch (error) {
      console.error('[DeviceAdapter] 连接失败:', error)
      throw error
    }
  }

  /**
   * 打开设备
   * @returns {Promise<void>}
   */
  async open() {
    try {
      await this.device.open()
    } catch (error) {
      console.error('[DeviceAdapter] 打开设备失败:', error)
      throw error
    }
  }

  /**
   * 发送数据
   * @param {number} reportId
   * @param {Uint8Array} data
   * @returns {Promise<void>}
   */
  async sendReport(reportId, data) {
    try {
      await this.device.sendReport(reportId, data)
    } catch (error) {
      console.error('[DeviceAdapter] 发送数据失败:', error)
      throw error
    }
  }

  /**
   * 设置数据接收回调
   * @param {Function} callback
   */
  set oninputreport(callback) {
    this.device.oninputreport = callback
  }

  /**
   * 获取数据接收回调
   */
  get oninputreport() {
    return this.device.oninputreport
  }

  /**
   * 设置语言切换回调
   * @param {Function} callback
   */
  set onlanguagechange(callback) {
    this.device.onlanguagechange = callback
  }

  /**
   * 获取语言切换回调
   */
  get onlanguagechange() {
    return this.device.onlanguagechange
  }

  /**
   * 设置设备信息变化回调
   * @param {Function} callback
   */
  set ondevicechange(callback) {
    this.device.ondevicechange = callback
  }

  /**
   * 获取设备信息变化回调
   */
  get ondevicechange() {
    return this.device.ondevicechange
  }

  /**
   * 关闭设备
   * @returns {Promise<void>}
   */
  async close() {
    try {
      await this.device.close()
    } catch (error) {
      console.error('[DeviceAdapter] 关闭设备失败:', error)
    }
  }

  /**
   * 获取当前模式
   * @returns {string} 'webhid' 或 'websocket'
   */
  getMode() {
    return this.mode
  }

  /**
   * 获取设备信息
   * @returns {Object}
   */
  getDeviceInfo() {
    const info = this.device.getDeviceInfo()
    return {
      mode: this.mode,
      ...info
    }
  }

  /**
   * 获取产品名称
   */
  get productName() {
    return this.device.productName
  }

  /**
   * 获取 Vendor ID
   */
  get vendorId() {
    return this.device.vendorId
  }

  /**
   * 获取 Product ID
   */
  get productId() {
    return this.device.productId
  }

  /**
   * 获取打开状态
   */
  get isOpened() {
    return this.device.isOpened
  }

  /**
   * 注册网页需要监听的命令（仅 WebSocket 模式）
   * @param {Array<number>} commandHeader - 命令头数组，如 [0x64, 0x65, 0x66]
   * @returns {Promise<void>}
   */
  async registerCommand(commandHeader) {
    if (this.mode !== 'websocket') {
      console.warn('[DeviceAdapter] registerCommand 仅在 WebSocket 模式下可用')
      return
    }

    try {
      await this.device.registerCommand(commandHeader)
    } catch (error) {
      console.error('[DeviceAdapter] 注册命令失败:', error)
      throw error
    }
  }

  /**
   * 批量注册多个命令（仅 WebSocket 模式）
   * @param {Array<Array<number>>} commands - 多个命令头数组
   * @returns {Promise<void>}
   */
  async registerCommands(commands) {
    if (this.mode !== 'websocket') {
      console.warn('[DeviceAdapter] registerCommands 仅在 WebSocket 模式下可用')
      return
    }

    try {
      await this.device.registerCommands(commands)
    } catch (error) {
      console.error('[DeviceAdapter] 批量注册命令失败:', error)
      throw error
    }
  }
}
